import { DEFAULT_ROUND_CONFIG, type Attempt } from '@ranked-waiting/game-core';
import { renderPageShell } from '../lib/page-shell';
import { renderRoundStage } from '../lib/round-stage';
import { formatDurationMs, formatErrorMs, formatSignedOffsetMs } from '../lib/format';
import { escapeHtml } from '../lib/format';
import { resultBadgeMarkup, errorPercent, resultTier } from '../lib/result-badge';
import { accuracyBarMarkup } from '../lib/accuracy-bar';
import { ApiError, fetchMe } from '../lib/session';
import {
  armDailyAttempt,
  fetchDailyLeaderboard,
  startDailyChallenge,
  stopDailyAttempt,
  submitDailyChallenge,
  type DailyLeaderboardRow,
} from '../lib/daily-challenge';
import type { RouteHandler } from '../lib/router';

/** How accurate an attempt was, as a friendly 0-100% (100% = dead on the target). */
function accuracyPercent(attempt: Attempt): number {
  if (attempt.isFalseStart) return 0;
  return Math.max(0, 100 - errorPercent(attempt));
}

export const renderDailyChallenge: RouteHandler = (container, navigate) => {
  const body = renderPageShell(container, navigate, 'play', {
    title: 'DAILY CHALLENGE',
    subtitle: 'Free, no sign-in needed. Same target for everyone today. 3 attempts, lowest total error wins.',
    body: `
      <div class="challenge-layout">
        <div id="daily-mount" class="challenge-main"></div>
        <aside class="challenge-attempts" id="daily-attempts" aria-label="Your attempts">
          <h2>Your attempts</h2>
          <ol class="attempt-log" id="daily-attempt-log"></ol>
        </aside>
      </div>
    `,
  });

  const mount = body.querySelector<HTMLDivElement>('#daily-mount')!;
  const attemptLog = body.querySelector<HTMLOListElement>('#daily-attempt-log')!;
  const liveAttempts: Attempt[] = [];
  let totalAttempts = 3;
  let destroyRound: (() => void) | void;
  let destroyed = false;

  function cleanup() {
    destroyed = true;
    if (destroyRound) destroyRound();
  }

  /** The persistent right-hand record. Redrawn after every attempt so Attempt 1 and 2 stay visible while you play Attempt 3. */
  function renderAttemptLog() {
    const rows: string[] = [];
    for (let i = 0; i < totalAttempts; i += 1) {
      const attempt = liveAttempts[i];
      if (!attempt) {
        rows.push(`
          <li class="attempt-log__row attempt-log__row--pending">
            <span class="attempt-log__index">Attempt ${i + 1}</span>
            <span class="attempt-log__pending">Not played yet</span>
          </li>
        `);
        continue;
      }
      if (attempt.isFalseStart) {
        rows.push(`
          <li class="attempt-log__row" data-tier="false-start">
            <span class="attempt-log__index">Attempt ${i + 1}</span>
            <span class="attempt-log__false">False start</span>
          </li>
        `);
        continue;
      }
      rows.push(`
        <li class="attempt-log__row" data-tier="${resultTier(attempt)}">
          <span class="attempt-log__index">Attempt ${i + 1}</span>
          <dl class="attempt-log__stats">
            <div><dt>Time</dt><dd>${formatDurationMs(attempt.elapsedMs)}s</dd></div>
            <div><dt>Diff</dt><dd>${formatSignedOffsetMs(attempt.elapsedMs, attempt.targetMs)}</dd></div>
            <div><dt>Accuracy</dt><dd>${accuracyPercent(attempt).toFixed(1)}%</dd></div>
          </dl>
        </li>
      `);
    }
    attemptLog.innerHTML = rows.join('');
  }

  function renderLoading() {
    mount.innerHTML = `<div class="handoff"><p class="handoff__label">Loading today's challenge…</p></div>`;
  }

  function renderAlreadyPlayed() {
    mount.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Already played today</p>
        <h2>Come back tomorrow for a new target</h2>
        <div class="hero-actions">
          <button class="button button--secondary" type="button" data-action="leaderboard">View today's leaderboard</button>
        </div>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="leaderboard"]')?.addEventListener('click', () => {
      void renderLeaderboardOnly();
    });
  }

  function renderError(message: string) {
    mount.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Error</p>
        <h2>${escapeHtml(message)}</h2>
        <button class="button button--secondary" type="button" data-action="retry">Try again</button>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="retry"]')?.addEventListener('click', () => void begin());
  }

  async function renderLeaderboardOnly() {
    mount.innerHTML = `<div class="handoff"><p class="handoff__label">Loading leaderboard…</p></div>`;
    try {
      const board = await fetchDailyLeaderboard();
      renderLeaderboard(board.challengeDate, board.leaderboard, null);
    } catch (error) {
      renderError(error instanceof Error ? error.message : 'Could not load the leaderboard.');
    }
  }

  function renderLeaderboard(challengeDate: string, rows: DailyLeaderboardRow[], myDisplayName: string | null) {
    mount.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">${escapeHtml(challengeDate)} leaderboard</p>
        <ol class="attempt-list">
          ${
            rows.length === 0
              ? `<li class="attempt-list__empty">Nobody has submitted yet. Be the first.</li>`
              : rows
                  .map(
                    (row) =>
                      `<li class="${myDisplayName && row.displayName === myDisplayName ? 'is-false-start' : ''}">
                        <span>#${row.rank}</span>
                        <span>${escapeHtml(row.displayName)}</span>
                        <strong>${formatErrorMs(row.totalErrorMs)}</strong>
                      </li>`,
                  )
                  .join('')
          }
        </ol>
        <div class="hero-actions">
          <button class="button button--secondary" type="button" data-action="menu">Back to menu</button>
        </div>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="menu"]')?.addEventListener('click', () => navigate('practice'));
  }

  async function begin() {
    renderLoading();
    const me = await fetchMe().catch(() => null);

    let session: { sessionId: string; targetMs: number; attempts: number; challengeDate: string };
    try {
      session = await startDailyChallenge();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        renderAlreadyPlayed();
        return;
      }
      renderError(error instanceof Error ? error.message : 'Could not start today\'s challenge.');
      return;
    }
    if (destroyed) return;

    totalAttempts = session.attempts;
    liveAttempts.length = 0;
    renderAttemptLog();
    playAttempt(session.sessionId, session.targetMs, session.attempts, 0, session.challengeDate, me?.user.username ?? null);
  }

  function renderAttemptResult(
    attempt: Attempt,
    attemptIndex: number,
    onContinue: () => void,
  ) {
    mount.innerHTML = `
      <div class="round-result-pair round-result-pair--pop">
        <p class="handoff__label">Attempt ${attemptIndex + 1} of ${totalAttempts}</p>
        <div class="round-result${attempt.isFalseStart ? ' round-result--false-start' : ''}">
          <div><span>Result</span>${resultBadgeMarkup(attempt)}</div>
        </div>
        ${accuracyBarMarkup(attempt)}
        <button class="button button--primary" type="button" data-action="continue">
          ${attemptIndex + 1 < totalAttempts ? 'Next attempt' : 'See your total'}
        </button>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="continue"]')?.addEventListener('click', onContinue);
  }

  function playAttempt(
    sessionId: string,
    targetMs: number,
    total: number,
    attemptIndex: number,
    challengeDate: string,
    signedInUsername: string | null,
  ) {
    destroyRound = renderRoundStage(mount, {
      config: DEFAULT_ROUND_CONFIG,
      fixedTargetMs: targetMs,
      label: `Attempt ${attemptIndex + 1} of ${total}`,
      onArm: () => {
        void armDailyAttempt(sessionId, attemptIndex);
      },
      onStop: (attempt) => {
        liveAttempts[attemptIndex] = attempt;
        if (destroyRound) destroyRound();
        destroyRound = undefined;

        // Show the result the instant the button is released; the sidebar
        // updates with it too. The server submit for this attempt runs in the
        // background so the reveal never waits on a round trip (owner report
        // 2026-08-18: the ~2s stall before the result appeared).
        renderAttemptLog();
        renderAttemptResult(attempt, attemptIndex, () => {
          if (attemptIndex + 1 < total) {
            playAttempt(sessionId, targetMs, total, attemptIndex + 1, challengeDate, signedInUsername);
          } else {
            void finish(sessionId, challengeDate, signedInUsername);
          }
        });

        void stopDailyAttempt(
          sessionId,
          attemptIndex,
          attempt.isFalseStart ? null : attempt.elapsedMs,
          attempt.isFalseStart,
        ).catch(() => {
          /* best-effort: the final submit re-validates the session server-side */
        });
      },
    });
  }

  async function finish(sessionId: string, challengeDate: string, signedInUsername: string | null) {
    const totalErrorMs = liveAttempts.reduce((sum, a) => sum + a.errorMs, 0);

    if (signedInUsername) {
      try {
        await submitAndShowResult(sessionId, undefined, challengeDate);
      } catch (error) {
        renderError(error instanceof Error ? error.message : 'Could not submit your score.');
      }
      return;
    }

    mount.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Total error</p>
        <h2>${formatErrorMs(totalErrorMs)}</h2>
        <form class="choice-card" id="daily-name-form" aria-labelledby="daily-name-heading">
          <strong id="daily-name-heading">Enter a name for the leaderboard</strong>
          <input class="join-input" id="daily-name-input" maxlength="24" placeholder="Your name" aria-label="Display name" autocomplete="off" required />
          <button class="button button--primary" type="submit">Submit score</button>
          <p class="form-error" data-role="status" aria-live="polite"></p>
        </form>
      </div>
    `;
    const form = mount.querySelector<HTMLFormElement>('#daily-name-form')!;
    const input = mount.querySelector<HTMLInputElement>('#daily-name-input')!;
    const status = form.querySelector<HTMLParagraphElement>('[data-role="status"]')!;
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]')!;
      submitButton.disabled = true;
      void submitAndShowResult(sessionId, input.value, challengeDate).catch((error) => {
        status.textContent = error instanceof Error ? error.message : 'Could not submit your score.';
        submitButton.disabled = false;
      });
    });
  }

  async function submitAndShowResult(sessionId: string, displayName: string | undefined, challengeDate: string) {
    const result = await submitDailyChallenge(sessionId, displayName);
    const board = await fetchDailyLeaderboard(challengeDate);
    renderLeaderboard(challengeDate, board.leaderboard, result.displayName);
  }

  renderAttemptLog();
  void begin();

  return () => cleanup();
};
